'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useWriteTransition } from '@/lib/refresh-gate';
import { createBoardAction, createLensAction } from '@/app/actions';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useViewport } from '@/components/ui/use-viewport';
import { errorMessage } from '@/domain/types';
import type { PlainKey } from '@/i18n';
import { useT } from '@/lib/locale-context';
import styles from './create-view.module.css';

/**
 * The create act for views and boards (docs/LENSES.md §7, docs/VIEWS.md §3):
 * TWO honest questions - View or Board, and a name. Everything else -
 * audience, criteria, layout, columns, flow link - is configured ON the
 * created thing (create-then-configure). On success we land there.
 *
 * Birth defaults (the create actions' contract, never asked here):
 * - a VIEW is born with one grant, its creator's owner grant, so its creator
 *   alone reaches it, and its config is authored on /views/<id>.
 * - a BOARD is born on the kanban layout, curated (pins in, starts empty),
 *   with the same one owner grant as its audience (docs/VIEWS.md §4). Its
 *   layout is the opener's to switch in its head, and its owners' default is
 *   set in Configure, so the layout is no question here.
 *
 * Fully controlled (the caller owns `open`): the nav's two header "+"s and the
 * palette mount it on demand with the matching `initial` choice.
 */

const WHAT_HINT: Record<'lens' | 'view', PlainKey> = {
  lens: 'create.view.hintLens',
  view: 'create.view.hintView',
};

export function CreateView({
  open,
  onOpenChange,
  initial = 'view',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which answer to question one is preselected: the Lenses header's "+"
   *  opens on `lens`, the Views header's (and the palette/dock) on `view`. */
  initial?: 'lens' | 'view';
}) {
  const t = useT();
  const { compact } = useViewport();
  const [what, setWhat] = useState<'lens' | 'view'>(initial);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, start] = useWriteTransition();
  const router = useRouter();

  // Fresh draft on every open (the caller flips the prop, so reset on the
  // rising edge); a cancelled attempt never haunts the next.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setWhat(initial);
      setName('');
      setError(null);
    }
    prevOpenRef.current = open;
  }, [open, initial]);

  function submit() {
    const n = name.trim();
    if (n.length === 0) {
      setError(
        what === 'lens' ? t('create.view.nameMissingLens') : t('create.view.nameMissingView'),
      );
      return;
    }
    start(async () => {
      try {
        const res =
          what === 'lens'
            ? await createLensAction({ name: n })
            : await createBoardAction({ name: n });
        if (!res.ok) {
          setError(t.msg(errorMessage(res.error)));
          return;
        }
        const id = res.id;
        if (!id) return;
        onOpenChange(false);
        // No refresh after it: the action revalidated, and a navigation that
        // overtakes a revalidating action makes the router refresh once it
        // lands (Next's action queue, `needsRefresh`).
        router.push(what === 'lens' ? `/views/${id}` : `/boards/${id}`);
      } catch {
        setError(t('create.error.unreachable'));
      }
    });
  }

  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      busy={pending}
      title={what === 'lens' ? t('create.view.titleLens') : t('create.view.titleView')}
      testId="create-view-modal"
      footer={
        <>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          <Button variant="minimal" size="sm" pending={pending} onClick={() => onOpenChange(false)}>
            {t('create.view.cancel')}
          </Button>
          <Button
            variant="accent"
            size="sm"
            pending={pending}
            onClick={submit}
            data-testid="create-view-submit"
          >
            {what === 'lens' ? t('create.view.createLens') : t('create.view.createBoard')}
          </Button>
        </>
      }
    >
      {/* Question one: View, or Board. One short hint follows the ACTIVE
          choice only (DESIGN_SYSTEM.md "Authoring surfaces"). */}
      <Field label={t('create.view.kind')} layout="row" hint={t(WHAT_HINT[what])}>
        <SegmentedControl
          size="sm"
          ariaLabel={t('create.view.kindLabel')}
          value={what}
          options={[
            { value: 'lens', label: t('create.view.lens') },
            { value: 'view', label: t('create.view.view') },
          ]}
          onChange={(v) => setWhat(v)}
        />
      </Field>

      {/* Question two: the name. */}
      <Field label={t('create.view.name')} layout="row">
        <Input
          size="sm"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={
            what === 'lens'
              ? t('create.view.namePlaceholderLens')
              : t('create.view.namePlaceholderView')
          }
          aria-label={
            what === 'lens' ? t('create.view.nameLabelLens') : t('create.view.nameLabelView')
          }
          // Keyboard-first on desktop only: on compact the sheet opens to be
          // read, and a focused field would raise the keyboard with it.
          autoFocus={!compact}
          disabled={pending}
          invalid={!!error && name.trim().length === 0}
          data-testid="create-view-name"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
      </Field>
    </Dialog>
  );
}
